import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// LocalStorageContext to hold all key-value pairs as strings
type LocalStorageState = Record<string, string>;
type LocalStorageContextType = {
  state: LocalStorageState;
  setItem: (key: string, value: string) => void;
};

const LocalStorageContext = createContext<LocalStorageContextType | undefined>(
  undefined
);

export const LocalStorageProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<LocalStorageState>(() => {
    // Load all keys from localStorage as strings
    const keys = Object.keys(localStorage);
    const initial: LocalStorageState = {};
    keys.forEach((key) => {
      const item = localStorage.getItem(key);
      if (item !== null) {
        initial[key] = item;
      }
    });
    return initial;
  });

  const setItem = (key: string, value: string) => {
    setState((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, value);
  };

  useEffect(() => {
    // Listen for localStorage changes from other tabs
    const handler = (e: StorageEvent) => {
      if (e.key && e.newValue !== null) {
        setState((prev) => ({
          ...prev,
          [e.key!]: e.newValue!,
        }));
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <LocalStorageContext.Provider value={{ state, setItem }}>
      {children}
    </LocalStorageContext.Provider>
  );
};

// useLocalStorage hook using context, handles JSON parsing/stringifying
export const useLocalStorage = <T,>(
  key: string | undefined,
  defaultValue: T,
  useJSON = false
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const ctx = useContext(LocalStorageContext);
  if (!ctx)
    throw new Error("useLocalStorage must be used within LocalStorageProvider");

  const { state, setItem } = ctx;
  const value = useMemo(() => {
    if (!key) {
      return defaultValue;
    }

    const raw = state[key];

    if (raw === undefined) {
      return defaultValue;
    } else if (useJSON) {
      try {
        return JSON.parse(raw);
      } catch {
        return defaultValue;
      }
    } else {
      return raw as T;
    }
  }, [key, state, defaultValue, useJSON]);

  if (!key) {
    return [defaultValue, () => {}];
  }
  return [
    value,
    (val) => {
      if (useJSON) {
        setItem(key, JSON.stringify(val));
      } else if (typeof val === "string") {
        setItem(key, val);
      }
    },
  ];
};
